import type { CompressImage } from "store/features/compress/types";
import { LinearProgress } from "components/common/LinearProgress/LinearProgress";
import styles from "./CompressCompare.module.css";
import { useCompressCompare } from "./useCompressCompare";
import { ToolTip } from "components/common/ToolTip/ToolTip";
import { truncateImageTitle } from "client/utils/truncateImageTitle";
import { DotAnimation } from "components/common/Animation/DotAnimation/DotAnimation";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import { capitalize } from "client/utils/capitalize";
import { usePreview } from "components/modules/Editors/Compressor/Preview/usePreview";

export interface CompressCompareProps {
    image: CompressImage;
}

export const CompressCompare = ({ image }: CompressCompareProps) => {
    const {
        value,
        handleChange,
        sliderRangeRef,
        imageLoadingStates,
        canvasRef,
        containerRef,
        compressedSrc,
        isLoadingOriginalImage,
        originalImageLoadingPercentage,
        canvasHeight,
    } = useCompressCompare(image);

    const { shouldApplyVerticalOptimizationForSmallScreen } = usePreview();

    const buildCompressIndicatorStateView = () => {
        if (image.compressed && image.state !== "compressing" && !imageLoadingStates[image.id]?.isLoading) {
            return (
                <>
                    <span className="capitalize">{translateFunc("compressed")}:</span>
                    <span className="font-semibold" data-testid={`compressed-size`}>{`${
                        image.compressed.size
                    } (${image.compressed?.quality > -1 ? "-" : "+"}${Math.abs(image.compressed?.quality)}%)`}</span>
                </>
            );
        } else if (image.state === "compressing") {
            return (
                <>
                    <span className="capitalize">{translateFunc("compressed")}:</span>
                    <span className="font-semibold capitalize" data-testid={`compressed-size`}>
                        {translateFunc("common:processing")}
                    </span>
                    <DotAnimation />
                </>
            );
        } else if (imageLoadingStates[image.id]?.isLoading) {
            return (
                <>
                    <span className="capitalize">{translateFunc("compressed")}:</span>
                    <span className="font-semibold" data-testid={`compressed-size`}>{`${capitalize(
                        translateFunc("common:loading"),
                    )} ${imageLoadingStates[image.id].progress}%`}</span>
                </>
            );
        } else {
            return <></>;
        }
    };

    return (
        <div className="relative lg:px-4 mx-auto h-full flex flex-col  text-black dark:text-white z-0">
            <div
                className={`${
                    shouldApplyVerticalOptimizationForSmallScreen &&
                    "absolute top-0 z-50  [text-shadow:_0px_2px_4px_rgb(1_1_1_/_1)] text-white"
                }  left-0 w-full  overflow-hidden`}
            >
                <div className="w-full text-sm lg:text-lg flex pb-4  gap-4 lg:gap-24">
                    <div className="flex justify-center lg:justify-end gap-2 w-1/2">
                        <span className="capitalize">{translateFunc("original")}:</span>
                        <span className="font-semibold" data-testid={`original-size`}>{`${image.original.size}`}</span>
                    </div>
                    <div className="flex justify-center lg:justify-start gap-2 w-1/2">
                        {buildCompressIndicatorStateView()}
                    </div>
                </div>
            </div>

            <div className="relative w-full lg:px-60 bg-irGray-200 dark:bg-darkSurface-100 flex-grow flex items-center justify-center">
                <div
                    className="absolute inset-0 flex items-center justify-center max-h-full overflow-hidden"
                    ref={containerRef}
                >
                    <div className="relative max-h-full h-full inline-flex items-center justify-center">
                        <img
                            ref={canvasRef}
                            className={`h-full w-full pointer-events-none ${isLoadingOriginalImage && "hidden"} z-20`}
                        />

                        {!isLoadingOriginalImage && (
                            <div className="absolute left-0 right-0 z-10 overflow-hidden">
                                <div
                                    className="relative w-full h-full overflow-hidden"
                                    style={{
                                        height: `${canvasHeight}px`,
                                    }}
                                >
                                    <div
                                        className="absolute inset-0 z-10"
                                        style={{
                                            backgroundImage: `url("/checked-bg.png")`,
                                        }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {isLoadingOriginalImage && (
                            <div
                                className="relative flex flex-col items-center justify-center gap-4"
                                data-testid={`original-image-loader`}
                            >
                                <div className="capitalize">
                                    <Translate
                                        keyName="loading-image-text"
                                        variables={{
                                            percentage: originalImageLoadingPercentage,
                                            size: image.original.size,
                                        }}
                                    />
                                </div>
                                <div className="absolute left-0 right-0 top-full mt-2 outline outline-irBlue-200">
                                    <LinearProgress text="" percentage={originalImageLoadingPercentage} />
                                </div>
                            </div>
                        )}

                        {!isLoadingOriginalImage && (
                            <div className="absolute left-0 right-0 z-30 overflow-hidden">
                                <div
                                    className="relative w-full h-full overflow-hidden"
                                    style={{
                                        height: `${canvasHeight}px`,
                                    }}
                                >
                                    <div
                                        className="absolute inset-0 z-10"
                                        style={{
                                            clipPath: `inset(0 0 0 ${value}%)`,
                                            backgroundImage: `url("/checked-bg.png")`,
                                        }}
                                    >
                                        {(!imageLoadingStates[image.id]?.isLoading ||
                                            (imageLoadingStates[image.id]?.isLoading &&
                                                imageLoadingStates[image.id]?.progress <= 95)) && (
                                            <div className="relative h-full">
                                                <img
                                                    src={compressedSrc}
                                                    key={`${image.id}-compressed-image`}
                                                    className="w-full h-full"
                                                    data-testid={`compressed-image`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="absolute inset-0 flex items-center z-10">
                                        <div className="relative w-full">
                                            <input
                                                type="range"
                                                className={`${styles.slider} w-full`}
                                                min={0}
                                                max={100}
                                                value={value}
                                                onChange={handleChange}
                                                ref={sliderRangeRef}
                                                data-testid={`compressor-preview-slider-input`}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className="absolute top-0 bottom-0 flex flex-col justify-center items-center pointer-events-none z-20 -translate-x-1/2"
                                        style={{
                                            left: `${value}%`,
                                        }}
                                    >
                                        <div
                                            className="h-full bg-white"
                                            style={{ width: "1px", boxShadow: "0px -1px 5px 1px rgba(0,0,0,0.4)" }}
                                        ></div>
                                        <div className="rounded-full border border-white flex-grow-0 flex-shrink-0 flex items-center justify-center p-1">
                                            <ThumbIcon />
                                        </div>
                                        <div
                                            className="h-full bg-white"
                                            style={{ width: "1px", boxShadow: "0px -1px 5px 1px rgba(0,0,0,0.4)" }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className={`${
                    shouldApplyVerticalOptimizationForSmallScreen &&
                    "absolute z-50  bottom-0 left-0 [text-shadow:_0px_2px_4px_rgb(1_1_1_/_1)] text-white"
                } text-center w-full`}
            >
                <ToolTip text={image.title}>
                    <div className="text-center pt-4" data-testid={`compressed-image-title`}>{`${truncateImageTitle(
                        image.title,
                    )}`}</div>
                </ToolTip>
            </div>
        </div>
    );
};

const ThumbIcon = () => (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_b_425_604)">
            <circle cx="28" cy="28" r="28" fill="black" fillOpacity="0.2" />
        </g>
        <path
            d="M34.8416 29.8916C34.7605 29.9671 34.6955 30.0582 34.6504 30.1594C34.6053 30.2606 34.5811 30.3698 34.5791 30.4806C34.5772 30.5914 34.5976 30.7014 34.6391 30.8041C34.6805 30.9069 34.7423 31.0002 34.8207 31.0785C34.899 31.1569 34.9923 31.2186 35.095 31.2601C35.1978 31.3016 35.3078 31.322 35.4186 31.3201C35.5294 31.3181 35.6386 31.2939 35.7398 31.2488C35.841 31.2037 35.9321 31.1387 36.0076 31.0576L38.7577 28.3075C38.9122 28.1528 38.999 27.9431 38.999 27.7245C38.999 27.5058 38.9122 27.2961 38.7577 27.1415L36.0076 24.3914C35.8512 24.2456 35.6444 24.1663 35.4306 24.1701C35.2169 24.1738 35.013 24.2604 34.8618 24.4116C34.7107 24.5627 34.6241 24.7667 34.6203 24.9804C34.6165 25.1941 34.6959 25.401 34.8416 25.5574L36.1836 26.8994L31.5745 26.8994C31.3557 26.8994 31.1458 26.9864 30.9911 27.1411C30.8364 27.2958 30.7494 27.5057 30.7494 27.7245C30.7494 27.9433 30.8364 28.1531 30.9911 28.3079C31.1458 28.4626 31.3557 28.5495 31.5745 28.5495L36.1836 28.5495L34.8416 29.8916V29.8916Z"
            fill="white"
        />
        <path
            d="M28.2746 19.1992C28.4934 19.1992 28.7032 19.2861 28.858 19.4409C29.0127 19.5956 29.0996 19.8054 29.0996 20.0242L29.0996 35.9748C29.0996 36.1937 29.0127 36.4035 28.858 36.5582C28.7032 36.713 28.4934 36.7999 28.2746 36.7999C28.0558 36.7999 27.8459 36.713 27.6912 36.5582C27.5365 36.4035 27.4495 36.1937 27.4495 35.9748L27.4495 20.0242C27.4495 19.8054 27.5365 19.5956 27.6912 19.4409C27.8459 19.2861 28.0558 19.1992 28.2746 19.1992V19.1992Z"
            fill="white"
        />
        <path
            d="M24.9748 28.5495C25.1936 28.5495 25.4034 28.4626 25.5582 28.3079C25.7129 28.1531 25.7998 27.9433 25.7998 27.7245C25.7998 27.5057 25.7129 27.2958 25.5582 27.1411C25.4034 26.9864 25.1936 26.8994 24.9748 26.8994L19.8156 26.8994L21.1576 25.5574C21.3034 25.401 21.3827 25.1941 21.3789 24.9804C21.3752 24.7667 21.2886 24.5627 21.1374 24.4116C20.9863 24.2604 20.7823 24.1738 20.5686 24.1701C20.3548 24.1663 20.148 24.2456 19.9916 24.3914L17.2415 27.1415C17.087 27.2961 17.0002 27.5058 17.0002 27.7245C17.0002 27.9431 17.087 28.1528 17.2415 28.3075L19.9916 31.0576C20.0671 31.1387 20.1582 31.2037 20.2594 31.2488C20.3606 31.2939 20.4699 31.3181 20.5806 31.3201C20.6914 31.322 20.8014 31.3016 20.9042 31.2601C21.0069 31.2186 21.1002 31.1569 21.1786 31.0785C21.2569 31.0002 21.3187 30.9069 21.3602 30.8041C21.4017 30.7014 21.422 30.5914 21.4201 30.4806C21.4181 30.3698 21.3939 30.2606 21.3488 30.1594C21.3037 30.0582 21.2387 29.9671 21.1576 29.8916L19.8156 28.5495L24.9748 28.5495V28.5495Z"
            fill="white"
        />
        <defs>
            <filter
                id="filter0_b_425_604"
                x="-4"
                y="-4"
                width="64"
                height="64"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
            >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feGaussianBlur in="BackgroundImage" stdDeviation="2" />
                <feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_425_604" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_backgroundBlur_425_604" result="shape" />
            </filter>
        </defs>
    </svg>
);
